import React, { useState } from 'react';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Table from '../../components/ui/Table';
import Modal from '../../components/ui/Modal';
import Input, { Select, Textarea } from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import { notifications, employees, stores } from '../../data/mockData';
import { Bell, Plus, Send } from 'lucide-react';

const typeLabels = { osg_alert: 'ОСГ Алерт', display_alert: 'Выкладка', expiry_alert: 'Срок годности', task_assigned: 'Задача назначена' };
const typeBadge = { osg_alert: 'osg', display_alert: 'display', expiry_alert: 'warning', task_assigned: 'primary' };

export default function Notifications() {
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', type: 'task_assigned', target: '' });

  const unread = notifications.filter(n => !n.isRead).length;

  const columns = [
    { key: 'title', label: 'Заголовок', render: (v, row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {!row.isRead && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#4A6CF7', flexShrink: 0 }} />}
        <span style={{ fontWeight: row.isRead ? 400 : 700 }}>{v}</span>
      </div>
    )},
    { key: 'body', label: 'Текст', render: v => <span style={{ fontSize: 12, color: '#6B7280' }}>{v}</span> },
    { key: 'type', label: 'Тип', render: v => <Badge variant={typeBadge[v] || 'gray'}>{typeLabels[v] || v}</Badge> },
    { key: 'target', label: 'Получатель', render: (v) => {
      const emp = employees.find(e => e.id === v);
      return <span style={{ fontSize: 12 }}>{emp ? emp.name : v}</span>;
    }},
    { key: 'isRead', label: 'Прочитано', render: v => <Badge variant={v ? 'success' : 'warning'}>{v ? '✓ Да' : '• Нет'}</Badge> },
    { key: 'createdAt', label: 'Дата', render: v => <span style={{ fontSize: 12, color: '#6B7280' }}>{new Date(v).toLocaleString('ru-RU')}</span> },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1A1A2E' }}>Уведомления</h1>
          <p style={{ color: '#8E8E93', fontSize: 13, marginTop: 2 }}>Всего: {notifications.length} • Непрочитано: {unread}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="secondary" icon={<Send size={14} />} onClick={() => setShowBroadcast(true)}>Рассылка</Button>
          <Button icon={<Plus size={14} />} onClick={() => setShowModal(true)}>Создать уведомление</Button>
        </div>
      </div>

      {/* Auto notifications settings */}
      <Card title="Автоматические уведомления">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { label: 'При критическом сроке ОСГ (≤3 дней)', enabled: true },
            { label: 'При назначении новой задачи', enabled: true },
            { label: 'При расхождении в инвентаризации', enabled: false },
            { label: 'При отклонении фото-отчёта', enabled: true },
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#F8F9FB', borderRadius: 8 }}>
              <span style={{ fontSize: 13, color: '#374151' }}>{item.label}</span>
              <div style={{
                width: 44, height: 24, borderRadius: 99, background: item.enabled ? '#4A6CF7' : '#D1D5DB',
                position: 'relative', cursor: 'pointer',
              }} onClick={() => toast('Настройка обновлена', 'success')}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: 3, left: item.enabled ? 23 : 3,
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card padding="0" title="История уведомлений">
        <Table columns={columns} data={notifications} emptyText="Нет уведомлений" />
      </Card>

      {/* Create notification modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Создать уведомление">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input label="Заголовок" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
          <Textarea label="Текст" value={form.body} onChange={e => setForm(f => ({ ...f, body: e.target.value }))} rows={3} required />
          <Select label="Тип" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} options={Object.entries(typeLabels).map(([k, v]) => ({ value: k, label: v }))} />
          <Select label="Получатель" value={form.target} onChange={e => setForm(f => ({ ...f, target: e.target.value }))} placeholder="Выберите сотрудника" options={employees.map(e => ({ value: e.id, label: e.name }))} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Отмена</Button>
            <Button onClick={() => { toast('Уведомление отправлено', 'success'); setShowModal(false); }}>Отправить</Button>
          </div>
        </div>
      </Modal>

      {/* Broadcast modal */}
      <Modal open={showBroadcast} onClose={() => setShowBroadcast(false)} title="Массовая рассылка">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input label="Заголовок" required />
          <Textarea label="Текст" rows={3} required />
          <Select label="Кому" options={[{ value: 'all', label: 'Всем сотрудникам' }, { value: 'store', label: 'По магазину' }, { value: 'role', label: 'По роли' }]} placeholder="Выберите получателей" />
          <Select label="Магазин" options={stores.map(s => ({ value: s.id, label: s.name }))} placeholder="Все магазины" />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <Button variant="secondary" onClick={() => setShowBroadcast(false)}>Отмена</Button>
            <Button icon={<Send size={14} />} onClick={() => { toast('Рассылка выполнена', 'success'); setShowBroadcast(false); }}>Разослать</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
